import update from "immutability-helper";
import { createStore, applyMiddleware } from "redux";
import { thunk } from "redux-thunk";
import { composeWithDevTools } from "@redux-devtools/extension";

import { uuidgen } from "@js/helpers/uuid.js";
import sha256 from "@js/helpers/sha256.js";

import { getPreviewHashForContent } from "@js/helpers/state.js";

import {
  CE_RENDER_PREVIEW_IMAGE_WAITING_FOR_PUBLISH,
  ceRequestNewRenderCycle,
  ceRenderPreviewImageNoContentToPreview,
} from "@js/actions/studio/render_preview_image.js";

import rootReducer from "@js/reducers/index";
import { ceBackgroundSave } from "@js/actions/studio/background_save.js";

// This is a non-action that simply makes sure our preview image
// subscriber fires on page load, before any user interaction
const CE_PAGE_START = "CE_PAGE_START";

export const initializeStore = (props) => {
  // Get the list of contents.

  let contents = props.contents;

  // Ensure we have at least one content.

  if (contents.length === 0) {
    contents = contents.concat([
      {
        id: null,
        name: "Content (1)",
        subject: "",
        format: "html",
        html: "",
        text: "",
        bee_json: null,
        active_html_editor: props.default_html_editor,
        published_sha256: {},
        validation_errors: null,
      },
    ]);
  }

  // Assign each pre-existing content a UUID.

  contents = contents.map((content) =>
    update(content, {
      client_content_id: {
        $set: content.client_content_id || uuidgen(),
      },
      preview_image: {
        $set: {
          step: "start",
          error_generation_id: null,
          image_url: null,
          preview_hash: null,
        },
      },
    }),
  );

  // Assign each pre-existing attachment a UUID.

  let attachments = props.attachments.map((attach) =>
    update(attach, {
      client_attachment_id: {
        $set: attach.client_attachment_id || uuidgen(),
      },
    }),
  );

  // Create the initial state.

  let initialState = {
    bee_token: null,
    campaign_id: props.campaign_id,
    organization_id: props.organization_id,
    mailing_list_id: props.mailing_list_id,
    mailing_lists: props.mailing_lists,
    bee_session_override: props.bee_session_override,
    sync: {
      // data_generation_id is incremented anytime we make a change to the
      // state which should result in a background sync
      data_generation_id: 1,
      // the last generation that was successfully communicated to server - it
      // may have had validation errors and is not actually in the database
      last_published_generation_id: 1,
      // indicate that the last publish included validation errors
      last_publish_had_validation_errors: false,
      // the number of consecutive failures the background sync has had due to
      // network errors (e.g. no connection, or the server returned a non-OK response)
      network_failure_count: 0,
      // background sync was stopped due to network failures
      network_failure_stopped: false,
      // there is a current background save running
      publish_in_progress: false,
      // the user has requested the error banner to be displayed (by clicking
      // on the background save status widget)
      show_error_banner: false,
    },
    data: {
      contents: contents,
      attachments: attachments,
    },
    screen: null,
    modal: null,
    library: {
      max_sizes: props.max_sizes,
      merge_tags: props.merge_tags,
      custom_fields: props.custom_fields,
      available_fields: props.available_fields,
      forms: props.forms,
      offer_v1_editor: props.offer_v1_editor,
      use_v1_editor_warning: props.use_v1_editor_warning,
      use_v1_editor: props.use_v1_editor,
      allow_editing: props.allow_editing,
      hugerte_config: props.hugerte_config,
      default_html_editor: props.default_html_editor,
    },
  };

  // Initialize the store.

  let store = createStore(
    rootReducer,
    initialState,
    composeWithDevTools(applyMiddleware(thunk, renderPreviewSubscriber)),
  );

  // Subscribe to changes so we can know when to start sync.

  store.subscribe(backgroundSyncSubscriber(store));
  store.subscribe(renderPreviewSubscriber(store));
  store.subscribe(hideScrollOnBodyWhenScreenIsActiveSubscriber(store));

  //Initialize the render process. Otherwise, the rendering won't start until the user has taken action
  store.dispatch({
    type: CE_PAGE_START,
  });

  // Since we initialize the page with a new blank content, we have to make sure it is saved
  // before opening an editor. Otherwise, image normalization will fail on the first save
  let contentsAreUnsaved = false;
  initialState.data.contents.forEach((content) => {
    if (!content.id) {
      // If any contents exist that are unsaved, flag the sync state as dirty so we can
      // trigger a save before proceeding to edit that
      contentsAreUnsaved = true;
    }
  });

  if (contentsAreUnsaved) {
    store.dispatch(ceBackgroundSave());
  }

  return store;
};

const backgroundSyncSubscriber = (store) => () => {
  let state = store.getState();
  let {
    data_generation_id,
    last_published_generation_id,
    network_failure_count,
    network_failure_stopped,
    publish_in_progress,
  } = state.sync;

  // Mark the page as dirty if we're not fully synced, regardless of network/publish status
  if (data_generation_id !== last_published_generation_id) {
    window.onbeforeunload = (e) => {
      return t("phrases.react_content.content_not_synced");
    };
  } else {
    window.onbeforeunload = null;
  }

  // Test if we're due to sync.
  if (
    data_generation_id === last_published_generation_id ||
    publish_in_progress ||
    network_failure_stopped
  )
    return;

  if (network_failure_count == 2) {
    setTimeout(() => {
      let {
        data_generation_id: new_data_generation_id,
        last_published_generation_id: new_last_published_generation_id,
        network_failure_stopped: new_network_failure_stopped,
        publish_in_progress: new_publish_in_progress,
      } = state.sync;

      // The generation we were on when the event happened is no longer
      // the current generation. We don't need to retry sync.
      if (data_generation_id !== new_data_generation_id) return;

      // Test if we should still sync.
      if (
        new_data_generation_id === new_last_published_generation_id ||
        new_publish_in_progress ||
        new_network_failure_stopped
      )
        return;

      // Tell the system to do a background save.
      store.dispatch(ceBackgroundSave());
    }, 5000);
  } else {
    // Tell the system to do a background save.
    store.dispatch(ceBackgroundSave());
  }
};

const renderPreviewSubscriber = (store) => (next) => (action) => {
  let returnValue = next(action);
  let state = store.getState();
  let contents = state.data.contents;
  let syncIsCaughtUp =
    state.sync.data_generation_id === state.sync.last_published_generation_id;

  // Short-circuit actions that were generated as part of the preview rendering
  // system. This prevents us from generating even more actions due to actions
  // generated by this middleware.
  if (action.type.indexOf("CE_RENDER_PREVIEW_IMAGE_") === 0) {
    return returnValue;
  }

  contents.forEach((content) => {
    // The content is published to the server if our local content matches the
    // sha256 of what is published. Because the format might have changed, we
    // also require that the background save be caught up.

    let contentPublishedToServer =
      content.format !== "text"
        ? sha256(content.html) === content.published_sha256.html
        : sha256(content.text) === content.published_sha256.text &&
          syncIsCaughtUp;

    let previewHash = getPreviewHashForContent(
      state,
      content.client_content_id,
    );

    // If the html or text is blank, then we don't need to render the image. We
    // shortcut that here.

    let hasContent =
      content.format === "text" ? !!content.text : !!content.html;

    if (!hasContent) {
      if (content.preview_image.step !== "no_content_to_preview") {
        store.dispatch(
          ceRenderPreviewImageNoContentToPreview(content.client_content_id),
        );
      }
      return;
    }

    // Decide if we're in a spot that we would want to render.

    let contentChanged = content.preview_image.preview_hash !== previewHash;
    let stepIsStart = content.preview_image.step === "start";
    let errorHasCleared =
      content.preview_image.step === "error" &&
      content.preview_image.error_generation_id !==
        state.sync.data_generation_id;
    let waitingForPublish =
      content.preview_image.step === "waiting_for_publish";

    if (contentChanged || stepIsStart || errorHasCleared || waitingForPublish) {
      // We have content for which we want to render a new preview image.

      if (contentPublishedToServer) {
        // Since the content has finished publishing, we can request a new preview image.
        store.dispatch(
          ceRequestNewRenderCycle(content.client_content_id, previewHash),
        );
      } else {
        // The content has not finished publishing. If we're not already
        // waiting, do so until we're caught up.
        if (!waitingForPublish) {
          store.dispatch({
            type: CE_RENDER_PREVIEW_IMAGE_WAITING_FOR_PUBLISH,
            client_content_id: content.client_content_id,
          });
        }
      }
    }
  });

  return returnValue;
};

const hideScrollOnBodyWhenScreenIsActiveSubscriber = (() => (store) => {
  let screenIsActive = false;

  return () => {
    let state = store.getState();

    if (state.screen) {
      if (!screenIsActive) {
        //Screen has just become active in this dispatch cycle
        document.body.style.overflow = "hidden";
        screenIsActive = true;
      }
    } else {
      if (screenIsActive) {
        //Screen just stopped being active in this dispatch cycle
        document.body.style.overflow = "";
        screenIsActive = false;
      }
    }
  };
})();
